import { adoptRefreshedToken, getStoredToken } from "./authStorage";

/**
 * VITE_API_BASE ถูกฝังตอน build เสมอ (deploy-cloudflare.yml → vars.WORKERS_API_URL)
 *
 * เคยมี fallback ที่ดูจาก hostname แล้วชี้ไป Workers API ตรง ๆ — ถอดออก 2026-07-17 ตอนย้ายบัญชี
 * เพราะมัน hardcode URL ของบัญชีเก่าไว้: SPA ที่ค้างใน cache จะยิงไปฐานเก่าเงียบ ๆ ต่อไปเรื่อย ๆ
 * ถ้า VITE_API_BASE หาย ให้คืน "" แล้วพังดัง ๆ (relative /api → 404 บน Pages) ดีกว่าเดาผิดฐาน
 */
export function getApiBase(): string {
  const env = import.meta.env.VITE_API_BASE;
  if (typeof env === "string" && env.trim() !== "") {
    return env.replace(/\/$/, "");
  }
  return "";
}

export type ApiResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
  meta?: Record<string, unknown>;
  /** HTTP status เมื่อคำขอไม่สำเร็จ (getJson) — ไม่มีค่าเมื่อเน็ตล้มก่อนได้คำตอบ · ใช้แยก 403 (ไม่มีสิทธิ์) ออกจาก 5xx/เน็ตหลุด */
  status?: number;
};

function authHeaders(): Record<string, string> {
  const t = getStoredToken();
  const h: Record<string, string> = {};
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

/**
 * ชื่อ event ที่ยิงบน window เมื่อ API ตอบ 401 กลางทาง (ไม่ได้ใช้งานเกินกำหนด · ครบอายุสูงสุด · เปลี่ยนรหัส/สิทธิ์ · ถูกปิดบัญชี)
 * AuthContext ดักไว้แล้วล้าง session ให้ — ผู้ใช้จะถูกพาไปหน้า login เองโดยไม่ต้องกดออกจากระบบ
 */
export const SESSION_EXPIRED_EVENT = "kk:session-expired";

/** ยิง event ครั้งเดียวพอ — หน้า dashboard เปิดหลาย panel พร้อมกัน ทุกตัวจะได้ 401 กลับมาพร้อม ๆ กัน */
let sessionExpiredFired = false;

/** เรียกหลังเข้าสู่ระบบสำเร็จ เพื่อให้รอบหน้าที่ token หมดอายุยิง event ได้อีก */
export function resetSessionExpiredGuard(): void {
  sessionExpiredFired = false;
}

function notifySessionExpired(res: Response, usedToken?: string | null): void {
  if (res.status !== 401 || sessionExpiredFired) return;
  // คำขอ login เองก็ตอบ 401 ตอนรหัสผ่านผิด — ห้ามนับเป็นเซสชันหมดอายุ
  // (ปัจจุบัน loginRequest ไม่ได้ผ่าน parseJson อยู่แล้ว เช็คซ้ำกันพลาดถ้ามีคนย้ายมาใช้ทีหลัง)
  if (res.url.includes("/api/auth/login")) return;
  // 401 ค้างท่อจาก token เก่า (query ช้าตอบกลับหลังผู้ใช้ login ใหม่แล้ว) — อย่าเตะ session ใหม่ทิ้ง
  if (usedToken !== undefined && usedToken !== getStoredToken()) return;
  sessionExpiredFired = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

async function parseJson<T>(res: Response, usedToken?: string | null): Promise<ApiResult<T>> {
  notifySessionExpired(res, usedToken);
  adoptRefreshedToken(res, usedToken);
  const text = await res.text();
  // Body ว่าง (204 หรือ PUT/DELETE ที่ไม่คืน body) — เดิมตกไป res.statusText แล้วผู้เรียก alert("OK")
  // ทั้งที่งานสำเร็จ · ตัดสินจาก HTTP status แทน
  if (text.trim() === "") {
    return res.ok ? { success: true } : { success: false, error: `HTTP ${res.status}` };
  }
  try {
    return JSON.parse(text) as ApiResult<T>;
  } catch {
    // ตอบไม่ใช่ JSON — ส่วนใหญ่คือหน้า HTML error ของ Cloudflare (502/504)
    // อย่ายัดทั้งหน้าเป็น error string เพราะผู้เรียกเอาไป alert() ทั้งก้อน
    return {
      success: false,
      error: text.trimStart().startsWith("<") ? `HTTP ${res.status}` : text,
    };
  }
}

/** ตัวเลือกเสริมของคำขอ
 *  - timeoutMs = ตัดคำขอที่ค้างเกินเวลา (สัญญาณมือถือสะดุดกลางทาง fetch จะรอไม่มีกำหนด → ปุ่มค้าง "กำลังบันทึก…")
 *    ถูกตัด = คืน error แบบไม่มี status เหมือนเน็ตหลุด (ผู้เรียกที่ลองใหม่ได้ เช่น ad-lab/casSave.ts ตรวจต่อเองว่าเขียนไปหรือยัง)
 *  - gzip = บีบ body ก่อนส่ง (เบราว์เซอร์ไม่บีบขาขึ้นให้เอง) · server ต้องรับ gzip ด้วย — ตอนนี้มีแค่ PUT /api/accounting/state
 *  - headers = header เพิ่ม (เช่น รุ่นของหน้า ad-lab · shared/adLabClientVersion.ts) — apiSend เท่านั้น */
export type RequestOpts = { timeoutMs?: number; gzip?: boolean; headers?: Record<string, string> };

function startTimeout(ms?: number): { signal?: AbortSignal; fired: () => boolean; clear: () => void } {
  if (!ms || ms <= 0) return { fired: () => false, clear: () => {} };
  const ctl = new AbortController();
  let fired = false;
  const t = setTimeout(() => { fired = true; ctl.abort(); }, ms);
  return { signal: ctl.signal, fired: () => fired, clear: () => clearTimeout(t) };
}

function timeoutMessage(ms?: number): string {
  return `รอ server นานเกิน ${Math.round((ms ?? 0) / 1000)} วินาที — เน็ตอาจสะดุด`;
}

/** เบราว์เซอร์นี้บีบ body ขาขึ้นได้ไหม (ผู้เรียกใช้เลือก timeout ให้พอกับขนาดที่ส่งจริง) */
export function canGzipUpload(): boolean {
  return typeof CompressionStream !== "undefined";
}

/** JSON → gzip (Blob) · เบราว์เซอร์ที่ไม่มี CompressionStream (iOS ก่อน 16.4) ส่ง JSON ธรรมดา — server รับได้ทั้งสองแบบ */
async function encodeBody(body: unknown, gzip?: boolean): Promise<string | Blob> {
  const json = JSON.stringify(body);
  if (!gzip || !canGzipUpload()) return json;
  return new Response(new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"))).blob();
}

/** Tiny POST/PATCH/DELETE helper used by mutation buttons across the admin.
 *  Kept thin on purpose — callers expect the same {success, error, data, id}
 *  envelope our Hono workers return. Moved here so lazy-loaded modal chunks
 *  can use it without pulling AdAccountsPage back into their bundle. */
export async function apiSend<T = unknown>(
  path: string,
  method: "PUT" | "POST" | "PATCH" | "DELETE",
  body?: unknown,
  opts: RequestOpts = {}
): Promise<{ success: boolean; error?: string; code?: string; id?: number; data?: T; status?: number }> {
  const token = getStoredToken();
  const timer = startTimeout(opts.timeoutMs);
  try {
    const payload = body ? await encodeBody(body, opts.gzip) : undefined;
    const res = await fetch(`${getApiBase()}${path}`, {
      method,
      headers: {
        "Content-Type": payload instanceof Blob ? "application/octet-stream" : "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      body: payload,
      signal: timer.signal,
    });
    // เดิมเรียก res.json() ตรง ๆ — เน็ตหลุดหรือได้หน้า HTML 502 กลับมาจะโยน TypeError/SyntaxError
    // ดิบ ๆ ใส่ผู้เรียก (ปุ่มค้าง busy) · ตอนนี้คืน envelope เดิมเสมอเหมือน getJson
    const parsed = await parseJson<T>(res, token);
    if (!res.ok) {
      // status ส่งต่อ (ไม่มี = เน็ตหลุด) — ผู้เรียกแยกได้ว่าลองใหม่ได้ไหม (เช่น ad-lab/casSave.ts)
      // code ส่งต่อด้วย (เช่น xian_other_team / duplicate / team_required) — ผู้เรียกแยกชนิดความผิดได้โดยไม่ต้องเดาจากข้อความ
      const code = (parsed as { code?: unknown }).code;
      return { success: false, error: parsed.error || `HTTP ${res.status}`, ...(typeof code === "string" ? { code } : {}), status: res.status };
    }
    return parsed as { success: boolean; error?: string; id?: number; data?: T };
  } catch (e) {
    if (timer.fired()) return { success: false, error: timeoutMessage(opts.timeoutMs) };
    const msg = e instanceof Error ? e.message : String(e);
    return { success: false, error: msg || "เชื่อมต่อ API ไม่สำเร็จ" };
  } finally { timer.clear(); }
}

export async function getJson<T>(path: string, opts: Pick<RequestOpts, "timeoutMs"> = {}): Promise<ApiResult<T>> {
  const timer = startTimeout(opts.timeoutMs);
  try {
    const url = `${getApiBase()}${path}`;
    const token = getStoredToken();
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: timer.signal,
    });
    const body = await parseJson<T>(res, token);
    if (!res.ok) {
      // meta ส่งต่อด้วย (เช่น fx/usd 503 แนบ meta.lastKnown) · data ไม่ส่ง กันผู้เรียกเผลอใช้ข้อมูลของคำขอที่พลาด
      return { success: false, error: body.error || `HTTP ${res.status}`, status: res.status, ...(body.meta ? { meta: body.meta } : {}) };
    }
    return body;
  } catch (e) {
    if (timer.fired()) return { success: false, error: timeoutMessage(opts.timeoutMs) };
    const msg = e instanceof Error ? e.message : String(e);
    return { success: false, error: msg || "เชื่อมต่อ API ไม่สำเร็จ" };
  } finally { timer.clear(); }
}

/**
 * getJson + auto-retry บน network failure (TypeError / "Failed to fetch") — ไม่ retry กับ HTTP error
 * ใช้กับหน้าที่ผู้ใช้รู้สึกว่า fetch หลุดบ่อย (เช่น admin/users) — ลด noise โดยไม่กระทบ pages อื่น
 *
 * + In-memory cache (in-tab) ที่ TTL สั้น ๆ — ทำให้ navigate กลับไปหน้าเดิมในระยะใกล้ได้ instant
 * (sync ของระบบ run ทุก 5 นาทีอยู่แล้ว 60s cache จึงปลอดภัย)
 */
type CacheEntry = { ts: number; result: ApiResult<unknown> };
const inFlight = new Map<string, Promise<ApiResult<unknown>>>();
const cacheStore = new Map<string, CacheEntry>();
// 5 นาที = หนึ่ง sync cycle ของ 168 · navigate ไป-กลับใน window นี้ไม่ยิง query เพิ่ม
const DEFAULT_TTL_MS = 5 * 60_000;

/** ลบ cache ทั้งหมด — ใช้ตอน logout หรือ user กดปุ่มรีเฟรช */
export function clearApiCache(): void {
  cacheStore.clear();
  inFlight.clear();
  // Notify subscribers (e.g. PaymentSearchPanel) ให้ลืม persisted state ของตัวเองด้วย
  for (const cb of clearListeners) cb();
}

/** Subscribe to cache clear events — module-level state ในที่อื่นที่ต้องลืมตอน logout */
const clearListeners = new Set<() => void>();
export function onApiCacheClear(cb: () => void): () => void {
  clearListeners.add(cb);
  return () => clearListeners.delete(cb);
}

/**
 * อ่าน cache แบบ synchronous — ใช้กับ useState initial เพื่อ render ด้วยข้อมูลจาก cache ทันที
 * ไม่ต้องผ่าน loading flash · return null ถ้าไม่มี cache หรือหมดอายุ
 */
export function readJsonCacheSync<T>(path: string, ttlMs?: number): ApiResult<T> | null {
  const ttl = ttlMs ?? DEFAULT_TTL_MS;
  const hit = cacheStore.get(path);
  if (hit && Date.now() - hit.ts < ttl) {
    return hit.result as ApiResult<T>;
  }
  return null;
}

export async function getJsonWithRetry<T>(
  path: string,
  opts: { retries?: number; delayMs?: number; ttlMs?: number; bypassCache?: boolean } = {}
): Promise<ApiResult<T>> {
  const retries = opts.retries ?? 1;
  const delayMs = opts.delayMs ?? 500;
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;

  // 1. cache hit (สด) → return ทันที
  if (!opts.bypassCache && ttl > 0) {
    const hit = cacheStore.get(path);
    if (hit && Date.now() - hit.ts < ttl) {
      return hit.result as ApiResult<T>;
    }
  }

  // 2. ถ้ามี request ที่ url นี้ค้างอยู่ → join เลย (de-dupe parallel callers)
  const existing = inFlight.get(path);
  if (existing && !opts.bypassCache) {
    return (await existing) as ApiResult<T>;
  }

  const task = (async (): Promise<ApiResult<T>> => {
    let last: ApiResult<T> = { success: false, error: "unknown" };
    for (let attempt = 0; attempt <= retries; attempt++) {
      last = await getJson<T>(path);
      if (last.success) {
        if (ttl > 0) cacheStore.set(path, { ts: Date.now(), result: last });
        return last;
      }
      // Retry เฉพาะ network failure จริงๆ — HTTP 4xx/5xx อย่า retry
      const err = (last.error || "").toLowerCase();
      const isNetwork =
        err.includes("failed to fetch") ||
        err.includes("networkerror") ||
        err.includes("load failed");
      if (!isNetwork) return last;
      if (attempt < retries) await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
    }
    return last;
  })();

  inFlight.set(path, task as Promise<ApiResult<unknown>>);
  try {
    return await task;
  } finally {
    inFlight.delete(path);
  }
}

/** แปล error message เป็นภาษาไทยที่ผู้ใช้ทำตามได้ — เน็ตหลุด, ระบบสะดุด (5xx), เซสชันหมดอายุ
 *  ข้อความไทยที่ API ส่งมาเองจะผ่านไปตามเดิม (ไม่แตะ) */
export function friendlyApiError(err: string | null | undefined): string {
  if (!err) return "เชื่อมต่อ API ไม่สำเร็จ";
  const e = err.toLowerCase();
  if (e.includes("failed to fetch") || e.includes("networkerror") || e.includes("load failed")) {
    return "เชื่อมต่อระบบไม่สำเร็จ — อินเทอร์เน็ตอาจหลุด หรือระบบเพิ่งเริ่มทำงาน อาจต้องรอครู่เดียวแล้วลองใหม่";
  }
  // ระบบฝั่งเซิร์ฟเวอร์สะดุด (5xx / D1 โหลดเกิน) — ไม่ใช่ความผิดผู้ใช้ บอกแค่ให้รอแล้วลองใหม่
  // ไม่เช็ค substring "503" เพราะ parseJson แปลง body ที่ไม่ใช่ JSON เป็น "HTTP 5xx" ให้แล้ว
  // และ substring จะไปโดนข้อความที่บังเอิญมีเลข 503 (เช่น ชื่อบัญชี/ยอดเงิน)
  if (/http 5\d\d/.test(e) || e.includes("internal server error")) {
    return "ระบบกำลังทำงานหนัก รอสักครู่แล้วลองใหม่อีกครั้ง";
  }
  // 401 จาก API — token หมดอายุ / ถูกยกเลิก (sessionToken.ts ฝั่ง API) หรือสิทธิ์ไม่พอ: บอกทางแก้ตรง ๆ แทนศัพท์ระบบ
  if (e.includes("invalid token") || e.includes("unauthorized")) {
    return "เซสชันหมดอายุ — กรุณาออกจากระบบแล้วเข้าสู่ระบบใหม่";
  }
  // 403 จาก API — ล็อกอินอยู่แต่ role ไม่ถึง: ออกจากระบบแล้วเข้าใหม่ไม่ช่วย ต้องให้แอดมินเปิดสิทธิ์
  if (e.includes("forbidden")) {
    return "คุณไม่มีสิทธิ์ใช้งานส่วนนี้ — ติดต่อผู้ดูแลระบบถ้าคิดว่าไม่ถูกต้อง";
  }
  if (e.includes("invalid credentials")) {
    return "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง";
  }
  return err;
}

export type MeUser = {
  id: number;
  username: string;
  role: string;
  display_name: string | null;
  group_id?: number | null;
  group_name?: string | null;
  can_view_all_teams?: boolean;
  visible_group_ids?: number[];
};

export type RoleName = "admin" | "manager" | "marketing" | "user";

/** Accepts any object with a `role` field — works with both MeUser and AuthUser */
export function isAdmin(user: { role?: string } | null | undefined): boolean {
  return user?.role === "admin";
}
export function isMarketing(user: { role?: string } | null | undefined): boolean {
  return user?.role === "marketing";
}
export function canAccessSalepages(user: { role?: string } | null | undefined): boolean {
  return user?.role === "admin" || user?.role === "marketing";
}

export type MeResponse = {
  success?: boolean;
  user?: MeUser;
  error?: string;
  /** HTTP status ของคำตอบ — ไม่มีค่าเมื่อ network ล้มก่อนได้คำตอบ */
  status?: number;
};

async function fetchMeOnce(): Promise<MeResponse> {
  try {
    const url = `${getApiBase()}/api/auth/me`;
    const usedToken = getStoredToken();
    const res = await fetch(url, {
      headers: { ...authHeaders(), Accept: "application/json" },
    });
    adoptRefreshedToken(res, usedToken);
    const text = await res.text();
    let body: MeResponse;
    try {
      body = JSON.parse(text) as MeResponse;
    } catch {
      return { success: false, error: text || res.statusText, status: res.status };
    }
    if (!res.ok) {
      return { success: false, error: body.error || `HTTP ${res.status}`, status: res.status };
    }
    return body;
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function fetchMe(): Promise<MeResponse> {
  let res = await fetchMeOnce();
  if (res.success === false && isNetworkError(res.error)) {
    await new Promise((r) => setTimeout(r, 500));
    res = await fetchMeOnce();
  }
  return res;
}

function isNetworkError(err: string | null | undefined): boolean {
  if (!err) return false;
  const e = err.toLowerCase();
  return e.includes("failed to fetch") || e.includes("networkerror") || e.includes("load failed");
}

export type LoginResponse = {
  success?: boolean;
  token?: string;
  user?: { id: number; username: string; role: string };
  error?: string;
};

async function loginRequestOnce(payload: Record<string, string>): Promise<LoginResponse> {
  try {
    const res = await fetch(`${getApiBase()}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const text = await res.text();
    try {
      return JSON.parse(text) as LoginResponse;
    } catch {
      return { success: false, error: text || res.statusText };
    }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

export async function loginRequest(
  username: string,
  password: string,
  turnstileToken?: string
): Promise<LoginResponse> {
  const payload: Record<string, string> = { username, password };
  if (turnstileToken) payload.turnstileToken = turnstileToken;
  // Retry บน network failure เท่านั้น — ไม่ retry กับ wrong password (HTTP 401)
  let res = await loginRequestOnce(payload);
  if (res.success === false && isNetworkError(res.error)) {
    await new Promise((r) => setTimeout(r, 500));
    res = await loginRequestOnce(payload);
  }
  return res;
}

export function buildQuery(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
